import { BaseService } from '../base';
import { mockBrokerUsersDb, mockBrokers } from './mock-data';
import {
  BrokerUser,
  BrokerUsersResponse,
  GetBrokerUsersParams,
  UpdateBrokerUserPayload,
  UpdateBrokerUserStatusPayload,
} from './types';

const useMock =
  (import.meta.env.VITE_ENABLE_BROKER_USERS_MOCK ?? 'true') === 'true';

const normalize = (value?: string) => (value || '').toLowerCase().trim();
const normalizeDigits = (value?: string) => (value || '').replace(/\D/g, '');

function paginate<T>(items: T[], page: number, pageSize: number) {
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  return items.slice(start, end);
}

function buildFilterOptions(source: BrokerUser[]) {
  const unique = <K extends keyof BrokerUser>(key: K) => {
    const seen = new Set<string>();
    return source
      .map(item => ({
        label:
          key === 'cpf'
            ? item.cpf
            : key === 'brokerId'
              ? item.brokerName
              : (item[key] as string),
        value: key === 'brokerId' ? item.brokerId : (item[key] as string),
      }))
      .filter(option => {
        const current = normalize(option.value);
        if (seen.has(current)) return false;
        seen.add(current);
        return Boolean(option.value);
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  return {
    brokers: mockBrokers.map(broker => ({
      label: broker.name,
      value: broker.id,
    })),
    names: unique('name'),
    emails: unique('email'),
    cpfs: unique('cpf'),
  };
}

function filterUsers(
  users: BrokerUser[],
  params: GetBrokerUsersParams
): BrokerUser[] {
  const { brokerId, name, email, cpf, search } = params;
  const normalizedName = normalize(name);
  const normalizedEmail = normalize(email);
  const normalizedCpf = normalizeDigits(cpf);
  const normalizedSearch = normalize(search);

  return users.filter(user => {
    const matchBroker = brokerId ? user.brokerId === brokerId : true;
    const matchName = normalizedName
      ? normalize(user.name).includes(normalizedName)
      : true;
    const matchEmail = normalizedEmail
      ? normalize(user.email).includes(normalizedEmail)
      : true;
    const matchCpf = normalizedCpf
      ? normalizeDigits(user.cpf).includes(normalizedCpf)
      : true;
    const matchSearch = normalizedSearch
      ? normalize(user.name).includes(normalizedSearch) ||
        normalize(user.email).includes(normalizedSearch) ||
        normalizeDigits(user.cpf).includes(normalizedSearch)
      : true;

    return matchBroker && matchName && matchEmail && matchCpf && matchSearch;
  });
}

export class BrokerUsersService extends BaseService {
  constructor() {
    super(import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000');
  }

  async listUsers(
    params: GetBrokerUsersParams = {}
  ): Promise<{ success: boolean; data?: BrokerUsersResponse; error?: string }> {
    if (useMock) {
      return this.listUsersMock(params);
    }

    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', String(params.page));
      if (params.pageSize)
        queryParams.append('pageSize', String(params.pageSize));
      if (params.brokerId) queryParams.append('brokerId', params.brokerId);
      if (params.name) queryParams.append('name', params.name);
      if (params.email) queryParams.append('email', params.email);
      if (params.cpf) queryParams.append('cpf', params.cpf);
      if (params.search) queryParams.append('search', params.search);

      const queryString = queryParams.toString();
      const response = await this.get(
        `/portal/v1/parties/brokers/users${queryString ? `?${queryString}` : ''}`
      );

      const data = await this.handleJsonResponse<BrokerUsersResponse>(response);
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  private async listUsersMock(
    params: GetBrokerUsersParams
  ): Promise<{ success: boolean; data: BrokerUsersResponse }> {
    const page = Number(params.page) > 0 ? Number(params.page) : 1;
    const pageSize = Number(params.pageSize) > 0 ? Number(params.pageSize) : 10;
    const hasBroker =
      !params.brokerId ||
      mockBrokers.some(broker => broker.id === params.brokerId);
    const appliedParams = hasBroker
      ? params
      : { ...params, brokerId: undefined };

    const filtered = filterUsers(mockBrokerUsersDb, appliedParams);
    const paginated = paginate(filtered, page, pageSize);
    const filters = buildFilterOptions(mockBrokerUsersDb);

    return Promise.resolve({
      success: true,
      data: {
        users: paginated,
        pagination: {
          page,
          pageSize,
          total: filtered.length,
          totalPages: Math.max(1, Math.ceil(filtered.length / pageSize)),
        },
        filters,
      },
    });
  }

  async updateStatus(
    payload: UpdateBrokerUserStatusPayload
  ): Promise<{ success: boolean; data?: BrokerUser; error?: string }> {
    if (useMock) {
      const userIndex = mockBrokerUsersDb.findIndex(
        user => user.id === payload.userId
      );
      if (userIndex === -1) {
        return Promise.resolve({
          success: false,
          error: 'Usuário não encontrado.',
        });
      }

      const updatedUser: BrokerUser = {
        ...mockBrokerUsersDb[userIndex],
        status: payload.nextStatus,
        lastStatusChangeAt: new Date().toISOString(),
        lastStatusChangeBy: payload.changedBy || 'Sistema',
      };

      mockBrokerUsersDb.splice(userIndex, 1, updatedUser);

      return Promise.resolve({ success: true, data: updatedUser });
    }

    try {
      const response = await this.post(
        '/portal/v1/parties/brokers/users/status',
        payload
      );
      return await this.handleJsonResponse<BrokerUser>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async updateUser(
    payload: UpdateBrokerUserPayload
  ): Promise<{ success: boolean; data?: BrokerUser; error?: string }> {
    if (useMock) {
      const userIndex = mockBrokerUsersDb.findIndex(
        user => user.id === payload.id
      );
      if (userIndex === -1) {
        return Promise.resolve({
          success: false,
          error: 'Usuário não encontrado.',
        });
      }

      const updatedUser: BrokerUser = {
        ...mockBrokerUsersDb[userIndex],
        ...payload,
      };

      mockBrokerUsersDb.splice(userIndex, 1, updatedUser);

      return Promise.resolve({ success: true, data: updatedUser });
    }

    try {
      const response = await this.put(
        '/portal/v1/parties/brokers/users',
        payload
      );
      return await this.handleJsonResponse<BrokerUser>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }
}

export const brokerUsersService = new BrokerUsersService();
